<template>
  <div :class="$style.taskActions">
    <button
      :class="[
        $style.actionButton,
        $style.completeButton,
        { [$style.completed]: task.completed },
      ]"
      @click.stop="handleComplete"
    >
      <CheckCircleIcon v-if="task.completed" :size="32" />
      <CheckIcon v-else :size="32" />
    </button>
    <button
      :class="[$style.actionButton, $style.detailsButton]"
      @click.stop="handleDetails"
    >
      <DetailsIcon :size="16" />
    </button>
    <button
      :class="[$style.actionButton, $style.deleteButton]"
      @click.stop="handleDelete"
    >
      <DeleteIcon :size="16" />
    </button>
  </div>
</template>

<script setup lang="ts">
import type { Task } from "../types/task";
import { CheckIcon, CheckCircleIcon, DetailsIcon } from "./icons";
import DeleteIcon from "./DeleteIcon.vue";

defineProps<{
  task: Task;
}>();

const emit = defineEmits<{
  (e: "complete"): void;
  (e: "edit"): void;
  (e: "delete"): void;
}>();

const handleComplete = () => {
  emit("complete");
};

const handleDetails = () => {
  emit("edit");
};

const handleDelete = () => {
  emit("delete");
};
</script>

<style module>
.taskActions {
  right: 10px;
  top: 50%;
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.taskActions:hover {
  opacity: 1;
}

@media (max-width: 1024px) {
  .taskActions {
    opacity: 1 !important;
  }
}

.actionButton {
  background: none;
  border: none;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  border-radius: 4px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Only apply hover effects on devices that support hover */
@media (hover: hover) and (pointer: fine) {
  .actionButton:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  .completeButton:hover {
    background: rgba(0, 184, 148, 0.2);
    color: #00b894;
  }

  .detailsButton:hover {
    background: rgba(116, 185, 255, 0.2);
    color: var(--task-meeting);
  }

  .deleteButton:hover {
    background: rgba(255, 118, 117, 0.2);
    color: var(--task-design);
  }
}

.completeButton.completed {
  color: #00b894;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .taskActions {
    right: 8px;
    gap: 4px;
  }

  .actionButton {
    min-width: 36px;
    min-height: 36px;
    padding: 0;
  }
}

@media (max-width: 480px) {
  .taskActions {
    right: 6px;
    gap: 3px;
  }
}
</style>
